import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Award, Calendar, ClipboardList, Plus, Infinity, Lock } from 'lucide-react';
import api from '@/lib/api';
import { useTheme } from '@/contexts/ThemeContext';
import { useSeason } from '@/contexts/SeasonContext';
import { StatCards, UpcomingMatches, RecentRequests, UpcomingRoundRequests } from '@/components/DashboardWidgets';
import { HorizontalBarCard } from '@/components/HorizontalBarRows';
import { ThinDonutCard } from '@/components/ThinDonutCard';
import { DashboardCustomizeBar, DashboardCustomizePanel, DashboardGrid } from '@/components/DashboardLayout';
import { useDashboardCustomization } from '@/hooks/useDashboardCustomization';
import { WIDGET_PERM } from '@/lib/dashboardPerms';

const STATUS_BAR_COLORS = {
  submitted: '#3B82F6', under_review: '#F3CF5B', approved: '#10B981',
  partially_approved: '#7EC9C9', rejected: '#E44E50', cancelled: '#6B7280',
  cancellation_requested: '#F59E0B', pending_sponsor_approval: '#6763A9', delivered: '#7EC9C9',
};

const defaultVis = { barChart: true, pieChart: true, upcomingMatches: true, upcomingRoundRequests: true, rightsBalances: true, recentRequests: true };
const defaultOrder = ['pieChart', 'barChart', 'recentRequests', 'upcomingRoundRequests', 'upcomingMatches', 'rightsBalances'];
const defaultSizes = { pieChart: 'small', barChart: 'small', recentRequests: 'small', upcomingRoundRequests: 'full', upcomingMatches: 'small', rightsBalances: 'large' };

const spoViewerVis = { upcomingMatches: true, upcomingRoundRequests: true, recentRequests: true };
const spoViewerOrder = ['upcomingMatches', 'upcomingRoundRequests', 'recentRequests'];
const spoViewerSizes = { upcomingMatches: 'full', upcomingRoundRequests: 'full', recentRequests: 'full' };

export default function SponsorDashboard() {
  const { t } = useTranslation();
  const { user, hasPerm } = useAuth();
  const { theme } = useTheme();
  const { seasonOpen } = useSeason();
  const isDark = theme === 'dark';
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const isSpoViewer = user?.role === 'spo_viewer';
  const [upcomingRound, setUpcomingRound] = useState(null);

  const customize = useDashboardCustomization(
    'isp_sponsor_dashboard',
    isSpoViewer ? spoViewerVis : defaultVis,
    isSpoViewer ? spoViewerOrder : defaultOrder,
    isSpoViewer ? spoViewerSizes : defaultSizes,
    (k) => hasPerm(WIDGET_PERM[k])
  );

  useEffect(() => {
    api.get('/api/dashboard/stats').then(r => setStats(r.data)).catch(() => {}).finally(() => setLoading(false));
    api.get('/api/dashboard/upcoming-round').then(r => setUpcomingRound(r.data)).catch(() => {});
  }, []);

  if (loading) return <div className="flex items-center justify-center h-64 text-gray-400">{t('loading')}</div>;

  const simpleCards = isSpoViewer ? [
    { label: t('upcoming_matches'), value: stats?.upcoming_matches || 0, icon: Calendar, color: 'text-purple-600', bg: 'bg-purple-50', link: '/matches' },
  ] : [
    { label: t('active_rights'), value: stats?.assignments?.length || 0, icon: Award, color: 'text-blue-600', bg: 'bg-blue-50', link: '/dashboard' },
    { label: t('upcoming_matches'), value: stats?.upcoming_matches || 0, icon: Calendar, color: 'text-purple-600', bg: 'bg-purple-50', link: '/matches' },
  ];
  const requestCard = {
    label: t('total_requests'), value: stats?.total_requests || 0, icon: ClipboardList, color: 'text-emerald-600', bg: 'bg-emerald-50', link: '/requests',
    breakdown: [
      { label: t('pending'), value: stats?.pending_requests || 0, dotColor: 'bg-amber-500' },
      { label: t('approved'), value: stats?.approved_requests || 0, dotColor: 'bg-green-500' },
      { label: t('rejected'), value: stats?.rejected_requests || 0, dotColor: 'bg-red-500' },
    ]
  };
  const barRawData = (stats?.assignments || [])
    .filter(a => a.allocation_type === 'limited').slice(0, 8)
    .map(a => {
      const used = a.used_amount || 0;
      const total = used + (a.remaining_amount || 0);
      return { name: a.right_name?.substring(0, 12), value: used, max: total || 1 };
    });
  const reqStatusData = Object.entries(stats?.request_status_breakdown || {})
    .filter(([, v]) => v > 0)
    .map(([key, value]) => ({
      name: key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
      value,
      color: STATUS_BAR_COLORS[key],
    }));
  if (reqStatusData.length === 0 && stats?.total_requests > 0) {
    if (stats.approved_requests) reqStatusData.push({ name: t('approved'), value: stats.approved_requests, color: STATUS_BAR_COLORS.approved });
    if (stats.pending_requests) reqStatusData.push({ name: t('pending'), value: stats.pending_requests, color: STATUS_BAR_COLORS.submitted });
    const other = (stats.total_requests || 0) - (stats.pending_requests || 0) - (stats.approved_requests || 0);
    if (other > 0) reqStatusData.push({ name: t('other'), value: other, color: '#6B7280' });
  }

  const widgetList = isSpoViewer ? [
    { key: 'upcomingMatches', label: t('upcoming_matches') || 'Upcoming Matches' },
    { key: 'upcomingRoundRequests', label: 'Upcoming Round Requests' },
    { key: 'recentRequests', label: t('recent_requests') },
  ] : [
    { key: 'barChart', label: t('rights_used_remaining') || 'Rights Usage' },
    { key: 'pieChart', label: t('requests_status') || 'Request Status' },
    { key: 'upcomingMatches', label: t('upcoming_matches') || 'Upcoming Matches' },
    { key: 'rightsBalances', label: t('my_rights_balances') || 'My Rights & Balances' },
    { key: 'upcomingRoundRequests', label: 'Upcoming Round Requests' },
    { key: 'recentRequests', label: t('recent_requests') },
  ];
  const allowedWidgetList = widgetList.filter(w => hasPerm(WIDGET_PERM[w.key]));

  const renderWidget = (key, size) => {
    switch (key) {
      case 'barChart': return <HorizontalBarCard title={t('rights_used_remaining')} data={barRawData} />;
      case 'pieChart': return <ThinDonutCard title={t('requests_status')} data={reqStatusData} />;
      case 'upcomingMatches': return <UpcomingMatches thisRoundMatches={stats?.this_round_matches} thisRound={stats?.this_round} nextRoundMatches={stats?.next_round_matches} nextRound={stats?.next_round} size={customize.widgetSizes['upcomingMatches'] || 'small'} />;
      case 'rightsBalances': {
        const isSmall = size === 'small';
        const gridCls = isSmall ? 'grid-cols-1' : size === 'large' ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3';
        return (
 <Card className="bg-white dark:bg-[var(--surface)] shadow-card rounded-card flex flex-col">
            <CardHeader className="pb-2 pt-5 px-5 shrink-0">
              <CardTitle className={`${isSmall ? 'text-sm lg:text-base' : 'text-sm lg:text-lg'} font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-2.5`}>
                <div className="p-1.5 rounded-lg bg-violet-50 dark:bg-violet-950">
                  <Award size={15} className="text-violet-500" />
                </div>
                {t('my_rights_balances')}
              </CardTitle>
            </CardHeader>
            <CardContent className="px-5 pb-5 pt-0">
              <div className={`grid ${gridCls} gap-3`}>
                {(stats?.assignments || []).map(a => {
                const isU = a.allocation_type === 'unlimited';
                const used = a.used_amount || 0;
                const remaining = a.remaining_amount || 0;
                const total = used + remaining;
                const usedPct = total > 0 ? Math.round((used / total) * 100) : 0;
                return (
                  <div key={a.id} className="card-row">
                    <div className={`${isSmall ? 'pt-2 pb-2 px-1' : 'pt-2.5 pb-2.5 px-1'} space-y-2`}>
                      <div className="flex items-start justify-between gap-1">
                        <p className={`${isSmall ? 'text-xs' : 'text-sm'} font-semibold text-gray-800 dark:text-gray-200 truncate flex-1 min-w-0`}>{a.right_name}</p>
                        <Badge className={`shrink-0 pointer-events-none select-none ${isSmall ? 'text-[9px] px-1 py-0' : ''} ${isU ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}`}>{isU ? t('unlimited') : t('limited')}</Badge>
                      </div>
                      {isU ? (
                        <div className="space-y-1">
                          <div className="flex items-center gap-2 text-emerald-600">
                            <Infinity size={isSmall ? 14 : 18} />
                            <span className={`${isSmall ? 'text-xs' : 'text-sm'} font-medium`}>{t('unlimited')}</span>
                          </div>
                          <p className={`${isSmall ? 'text-[10px]' : 'text-xs'} text-amber-600 font-semibold`}>{used} {t('used')}</p>
                        </div>
                      ) : (
                        <>
                          <p className={`${isSmall ? 'text-[10px]' : 'text-xs'} text-gray-500`}>{t('allocation')}: <span className="font-semibold text-gray-800 dark:text-gray-200">{a.allocated_amount}</span></p>
                          <div className="flex items-center gap-1.5 text-xs">
                            <span className={`text-amber-600 font-semibold whitespace-nowrap ${isSmall ? 'text-[10px]' : ''}`}>{used} {t('used')}</span>
                            <div className="flex-1 h-1.5 bg-gray-100 dark:bg-[var(--chip)] rounded-full overflow-hidden min-w-[20px]">
                              <div className="h-full bg-[var(--brand)] dark:bg-white rounded-full transition-all" style={{ width: `${usedPct}%` }} />
                            </div>
                            <span className={`text-blue-600 font-semibold whitespace-nowrap ${isSmall ? 'text-[10px]' : ''}`}>{remaining} {t('remaining')}</span>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
              </div>
            </CardContent>
          </Card>
        );
      }
      case 'upcomingRoundRequests': return <UpcomingRoundRequests data={upcomingRound} showSponsorTabs={isSpoViewer} />;
      case 'recentRequests': return <RecentRequests requests={stats?.recent_requests} showSponsor={isSpoViewer} size={customize.widgetSizes['recentRequests'] || 'full'} />;
      default: return null;
    }
  };

  return (
    <div data-testid="sponsor-dashboard" className="space-y-5 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl sm:page-title">{t('welcome_name', { name: user?.name?.split(' ')[0] || '' })}</h1>
          <p className="text-gray-400 mt-0.5 text-xs lg:text-sm">{user?.name}{isSpoViewer ? '' : ` - ${stats?.sponsor_name || ''}`}</p>
        </div>
        <div className="flex items-center gap-2">
          {!isSpoViewer && seasonOpen && (
            <Button data-testid="new-request-btn" onClick={() => navigate('/requests/new')} className="bg-gray-900 text-white hover:bg-gray-800 text-sm px-5 py-2.5">
              <Plus size={16} className="mr-1.5" /> {t('new_request')}
            </Button>
          )}
          <DashboardCustomizeBar {...customize} widgetList={allowedWidgetList} />
        </div>
      </div>
      <DashboardCustomizePanel {...customize} widgetList={allowedWidgetList} />
      {!seasonOpen && (
        <Card data-testid="season-closed-banner" className="bg-amber-50 dark:bg-amber-950 border-amber-200 dark:border-amber-800 shadow-none">
          <CardContent className="py-4 flex items-center gap-3">
            <Lock size={18} className="text-amber-600 dark:text-amber-400 shrink-0" />
            <div>
              <p className="text-sm font-semibold text-amber-800 dark:text-amber-300">Season is Currently Closed</p>
              <p className="text-xs text-amber-600 dark:text-amber-500 mt-0.5">The admin has closed the current season. Matches and new requests are unavailable until the season reopens.</p>
            </div>
          </CardContent>
        </Card>
      )}
      {hasPerm(WIDGET_PERM.statCards) && <StatCards cards={simpleCards} requestCard={requestCard} />}
      <DashboardGrid {...customize} renderWidget={renderWidget} />
    </div>
  );
}
